import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { Navigation, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FilterModel } from 'ag-grid-community';
import { from } from 'rxjs';
import { ApVendor } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { ApVendorService } from 'src/app/services/ap-vendor.service';
import { AuthService } from 'src/app/services/auth.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { apConfigsService } from 'src/app/shared/vive-api/ap-configs.service';
import { companiesService } from 'src/app/shared/vive-api/companies.service';
import { ApConfigDto } from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

import {
  getApVendorClientManagedColumnActions,
  isApVendorLinked
} from '../helpers/ap-vendor-grid-actions.helper';

@Component({
  selector: 'app-client-admin-accounting-all-ap-vendors',
  templateUrl: './client-admin-accounting-all-ap-vendors.component.html',
  styleUrls: ['./client-admin-accounting-all-ap-vendors.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientAdminAccountingAllApVendorsComponent {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private Router: Router,
    private SharedDataService: SharedDataService,
    private ModalService: NgbModal,
    private ApVendorService: ApVendorService,
    private ToastService: ToastService,
    private AuthService: AuthService
  ) {
    this.SharedDataService.listenForEvent<Navigation>().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'SetNavigationData' && data) {
          const {
            extras: { state }
          } = data;

          if (state) {
            this.handleStateData(state);
          }
        }
      }
    });

    this.SharedDataService.sendEvent('GetNavigationData');

    const currentNavigation = this.Router.currentNavigation();
    const state = currentNavigation?.extras.state;

    if (state) {
      this.handleStateData(state);
    }

    from(
      apConfigsService.findAllForClient({
        pathParams: { companyId: this.AuthService.getUserCompanyId() }
      })
    ).subscribe({
      next: ({ data: apConfigs }) => {
        this.applyApExemptColumnRules(apConfigs);
        this.columnRulesReady = true;
        this._cdr.markForCheck();
      }
    });
  }

  title: string = 'Ap Vendors';
  columnRulesReady: boolean = false;
  defaultHiddenColumns: string[] = ['isExempt'];
  suppressedColumns: string[] = [];
  suppliedGridQueryData: { [key: string]: any } = {
    attributes: ['ApConfigId', 'vendorKey'],
    aliasedAttributes: ['hasMetaData'],
    ApConfig: {
      attributes: [
        'id',
        'CompanyId',
        'apSystem',
        'showExemptChange',
        'v3CutOverDate'
      ]
    }
  };
  FilterModel?: FilterModel;
  reloadGrid: boolean = false;

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['invite', 'refresh']
  };

  /**
   * Same AP Exempt rules as the per-system Accounting grid:
   * - Vantaca-only: column not available
   * - Any non-Vantaca: column available but defaulted OFF
   */
  applyApExemptColumnRules(apConfigs: Pick<ApConfigDto, 'apSystem'>[]): void {
    const hasNonVantaca = apConfigs.some(
      (config) => config.apSystem !== 'Vantaca'
    );

    if (hasNonVantaca || apConfigs.length === 0) {
      this.suppressedColumns = [];
      this.defaultHiddenColumns = ['isExempt'];
    } else {
      this.suppressedColumns = ['isExempt'];
      this.defaultHiddenColumns = [];
    }
  }

  calculateColumnTooltipMap({
    rowData
  }: {
    rowData: any;
  }): null | Partial<{ [key in GridActionColumnActions]: string }> {
    // ApConfig can be absent (e.g. archived ApConfig filtered out of the
    // include) — fall back to the non-V3 tooltips instead of throwing (VPDW-3827).
    const isV3 =
      rowData.ApConfig?.apSystem === 'Vantaca' &&
      rowData.ApConfig?.v3CutOverDate &&
      rowData.ApConfig.v3CutOverDate <= new Date();

    return {
      add: isV3 ? 'Client Managed' : 'Add Exempt Flag',
      remove: isV3 ? 'VIVE Managed' : 'Remove Exempt Flag',
      invite: 'Invite Vendor',
      refresh: 'Refresh'
    };
  }

  calculatedColumnActions({
    rowData
  }: {
    rowData: any;
  }): GridActionColumnActions[] | null {
    const actions = getApVendorClientManagedColumnActions(rowData);

    return actions.length > 0 ? actions : null;
  }

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: ApVendor;
  }) {
    const { id, ApConfigId, vendorKey, InvitationContactId, ApConfig } =
      rowData;

    if (!id) {
      throw this.ToastService.addErrorToast('Error', 'No row ID found.', {
        returnError: true
      });
    }

    if (!ApConfig) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No Ap Config data found for this vendor.',
        {
          returnError: true
        }
      );
    }

    const { CompanyId, apSystem, v3CutOverDate } = ApConfig;

    const isV3 =
      apSystem === 'Vantaca' && v3CutOverDate && v3CutOverDate <= new Date();

    if (action === 'add' || action === 'remove') {
      if (apSystem !== 'Vantaca' || isApVendorLinked(rowData)) {
        return;
      }
    }

    if (action === 'add') {
      if (isV3) {
        from(
          companiesService.createApVendorMeta({
            pathParams: { companyId: CompanyId! },
            data: { ApConfigId: ApConfigId!, vendorKey: vendorKey! },
            options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully updated Ap Vendor..'
            );
            this.reloadGrid = true;
          }
        });
      } else {
        const apVendor = {
          id,
          isExempt: true
        };

        this.ApVendorService.saveApVendor(apVendor).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully added exempt flag.'
            );
            this.reloadGrid = true;
          }
        });
      }
    }

    if (action === 'remove') {
      if (isV3) {
        from(
          companiesService.deleteApVendorMetaByApConfigIdAndVendorKey({
            pathParams: {
              companyId: CompanyId!,
              apConfigId: ApConfigId!,
              vendorKey: vendorKey!
            },
            options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
          })
        ).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully updated Ap Vendor.'
            );
            this.reloadGrid = true;
          }
        });
      } else {
        const apVendor = {
          id,
          isExempt: false
        };

        this.ApVendorService.saveApVendor(apVendor).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully removed exempt flag.'
            );
            this.reloadGrid = true;
          }
        });
      }
    }

    if (action === 'invite') {
      if (InvitationContactId && InvitationContactId !== null) {
        const modalRef = this.ModalService.open(ConfirmModalComponent, {
          centered: true,
          backdrop: 'static'
        });

        Object.assign(modalRef.componentInstance, {
          displayText:
            'Invitation has already been sent. Are you sure you want to re-send invitation?'
        });

        modalRef.closed.subscribe({
          next: (confirm) => {
            if (confirm) {
              this.ApVendorService.inviteVendor(rowData).subscribe({
                next: () =>
                  this.ToastService.addSuccessToast(
                    'Success',
                    'Successfully resent vendor invitation.'
                  )
              });
            }
          }
        });
      } else {
        this.ApVendorService.inviteVendor(rowData).subscribe({
          next: () =>
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully sent vendor invitation.'
            )
        });
      }
    }

    if (action === 'refresh') {
      this.reloadGrid = true;
    }
  }

  handleStateData(state: GenericObject) {
    const { query, title, FilterModel } = state;

    if (title) {
      this.title = this.title.concat(` - ${title}`);
    }

    if (FilterModel) {
      this.FilterModel = FilterModel;
    }

    if (query) {
      Object.assign(this.suppliedGridQueryData, query);
    }
  }
}
